/// <amd-module name="Build/Stats/BuildMetricsSection" />

import * as React from "react";

import { Components, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { ITfsPageService } from "Tfs/Platform/Page";

import * as BuildContracts from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { PipelinesMetricsContainer, PipelinesMetricsPayload } from "Build/Stats/PipelinesMetricsContainer";
import * as Resources from "Build/Stats/Resources";

const enum MetricAggregationTypes {
    Hourly = "Hourly",
    Daily = "Daily"
}

const enum DefinitionMetrics {
    SuccessfulBuilds = "SuccessfulBuilds",
    FailedBuilds = "FailedBuilds",
    PartiallySuccessfulBuilds = "PartiallySuccessfulBuilds"
}

const enum BuildPerfConstants {
    Area = "BuildMetricsSection",
    Scenario = "FetchBuildMetrics"
}

interface BuildMetricsSectionProps extends IVssComponentProperties {
    numOfDays: number;
}

class BuildMetricsSection extends VssComponent<BuildMetricsSectionProps, {}> {
    public render(): JSX.Element | null {
        return (
            <PipelinesMetricsContainer
                succeededText={Resources.BuildsSucceeded}
                fetchMetrics={this.fetchMetrics}
                numOfDays={this.props.numOfDays}
            />
        );
    }

    public fetchMetrics = (numOfDays: number): Promise<PipelinesMetricsPayload | null> => {
        const client = this.context.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        let fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - numOfDays);

        let precision = MetricAggregationTypes.Daily;
        // build APIs are hourly precise only if date range is 1-3 days
        // for larger ranges they support only day precision
        if (numOfDays === 1) {
            precision = MetricAggregationTypes.Hourly;
        }

        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            performanceService.startScenario(BuildPerfConstants.Scenario, false);
            return client.getProjectMetrics(tfsPageData.project.id, precision, fromDate).then(
                (metrics: BuildContracts.BuildMetric[]) => {
                    if (metrics) {
                        performanceService.endScenario(BuildPerfConstants.Area, BuildPerfConstants.Scenario, false);
                        let passed: number = 0;
                        let failed: number = 0;

                        // Build sends daily metrics, so we need to sum up
                        // values for all the days and show
                        for (let metric of metrics) {
                            // The requirement is to treat partially successful also as passed
                            if (
                                metric.name === DefinitionMetrics.SuccessfulBuilds ||
                                metric.name === DefinitionMetrics.PartiallySuccessfulBuilds
                            ) {
                                passed += metric.intValue;
                            } else if (metric.name === DefinitionMetrics.FailedBuilds) {
                                failed += metric.intValue;
                            }
                        }

                        return {
                            passed: passed,
                            failed: failed
                        };
                    } else {
                        this._endPerfScenario(performanceService);
                    }
                    return null;
                },
                () => {
                    this._endPerfScenario(performanceService);
                    return null;
                }
            );
        } else {
            return Promise.resolve(null);
        }
    };

    private _endPerfScenario(performanceService: IVssPerformanceService): void {
        performanceService.endScenario(BuildPerfConstants.Area, BuildPerfConstants.Scenario, false, undefined, { status: "failed" });
    }
}

Components.add("Pipelines.BuildStats", BuildMetricsSection);
